import get from 'lodash/get'
import property from 'lodash/property'
import { createSelector } from 'reselect'
import { ACCOUNT } from '@signal/routes/core'
import isServer from '@signal/utils/env/isServer'
import {
  ACTIVATE_FEATURE_KEY,
  PERMISSION_LEVEL_WRITE,
  LEGACY_SIGNAL_PERMISSION_NAME_MAP,
  hasPermission
} from '@signal/utils/permissions'
import { cookUserAccountForSelector } from '../user/selectors'

export const rootSelector = property('auth')

export const isAuthenticatingSelector = createSelector(
  rootSelector,
  property('authenticating')
)
export const isAuthenticatedSelector = createSelector(
  rootSelector,
  property('authenticated')
)
export const isInitializedSelector = createSelector(
  rootSelector,
  property('isInitialized')
)
export const activeAccountSelector = createSelector(rootSelector, root =>
  // returns an object if on the server so react-snapshot can generate routes
  !isServer() ? get(root, 'activeAccount') : {}
)
export const hasActiveAccountSelector = createSelector(
  activeAccountSelector,
  Boolean
)

export const errorSelector = createSelector(rootSelector, property('error'))
export const errorMessageSelector = createSelector(
  errorSelector,
  property('message')
)

export const activeAccountIdRawSelector = createSelector(
  activeAccountSelector,
  property('accountId')
)

export const activeAccountFeaturesSelector = createSelector(
  activeAccountSelector,
  property('features')
)

export const activeAccountGroupSelector = createSelector(
  activeAccountSelector,
  property('group')
)

export const activeAccountSubGroupSelector = createSelector(
  activeAccountSelector,
  property('subGroup')
)

export const hasActivateWriteSelector = createSelector(
  activeAccountFeaturesSelector,
  features =>
    hasPermission(features, {
      feature: ACTIVATE_FEATURE_KEY,
      level: PERMISSION_LEVEL_WRITE
    })
)

export const activeAccountIdSelector = createSelector(
  activeAccountIdRawSelector,
  accountId => {
    if (isServer()) {
      return 'skeleton'
    }
    if (accountId) {
      return accountId
    }
    // sometimes we can call this from inside redux-saga, where it's not possible to easily use our cooked
    // simulated redux state.  In such instance, try to find the active accountId from the active route
    const accountMatch = ACCOUNT.match(window.location.pathname)
    return accountMatch && accountMatch.params.accountId
  }
)

export const activeAccountNameSelector = createSelector(
  activeAccountSelector,
  property('name')
)

/**
 * functions to create simulated redux state for data obtained by context fetcher for use with selectors
 */

export function cookActiveAccountForSelectors({ activeAccount }) {
  if (activeAccount) {
    const { accountName, accountId, ...rest } = activeAccount
    activeAccount = cookUserAccountForSelector({
      accountId,
      id: accountId,
      name: accountName,
      ...rest
    })
  }
  return {
    auth: {
      activeAccount
    }
  }
}

function generateFeatures(data = []) {
  return data
    .map(({ permissionName, level }) => ({
      feature: LEGACY_SIGNAL_PERMISSION_NAME_MAP[permissionName],
      level
    }))
    .filter(({ feature }) => feature)
}

export function cookPermissionsForSelectors({ response }) {
  if (!response) {
    return {}
  }

  let features = generateFeatures(response)

  if (response.length === 1 && response[0].access) {
    features = generateFeatures(response[0].access)
  }

  return {
    auth: {
      activeAccount: {
        features
      }
    }
  }
}
